/**
 * src/services/supportService.js
 *
 * Firestore-based support ticket service for Orrith - Cycle of Certainty.
 * Handles ticket CRUD for end users (create, list own, view, reply).
 * Help articles and FAQ are served from embedded data (see data/helpData.js).
 */

import { db } from "../lib/firebase";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  startAfter,
} from "../lib/firestoreClient";
import { sanitizeEmail, sanitizeText } from "../utils/sanitize";

const TICKETS = "support_tickets";

// ─── Create Ticket ──────────────────────────────────────────────

/**
 * Create a new support ticket.
 * @param {{ name?: string, email: string, subject: string, message: string, category?: string }} payload
 * @returns {Promise<{ id: string }>}
 */
export async function createTicket(user, payload) {
  if (!user) throw new Error("Authentication required");
  const uid = user.uid;

  const subject = sanitizeText(payload.subject, 160);
  const message = sanitizeText(payload.message, 2000);
  if (!subject || !message) {
    throw new Error("Subject and message are required");
  }

  const safeEmail = sanitizeEmail(payload.email || user.email || "");
  const safeName = sanitizeText(payload.name || user.displayName || "", 120);
  const safeCategory = sanitizeText(payload.category || "general", 40);

  const ticketDoc = {
    userId: user.uid,
    userEmail: safeEmail,
    userName: safeName,
    subject,
    category: safeCategory || "general",
    status: "open",
    replies: [
      {
        message,
        sentBy: user.uid,
        sentByEmail: user.email || "",
        isAdmin: false,
        createdAt: new Date().toISOString(),
      },
    ],
    lastMessageSnippet: message.substring(0, 120),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, TICKETS), ticketDoc);
  return { id: ref.id };
}

// ─── List User Tickets ──────────────────────────────────────────

/**
 * List current user's tickets with optional status filter.
 * @param {{ status?: string, max?: number }} opts
 * @returns {Promise<Array>}
 */
export async function listUserTickets(user, opts = {}) {
  if (!user) return [];
  const uid = user.uid;

  const constraints = [
    where("userId", "==", uid),
    orderBy("updatedAt", "desc"),
    limit(opts.max || 50),
  ];

  if (opts.status) {
    constraints.splice(1, 0, where("status", "==", opts.status));
  }

  const q = query(collection(db, TICKETS), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Real-time listener for current user's tickets.
 * @param {Function} callback
 * @param {string} [statusFilter]
 * @returns {Function} unsubscribe
 */
export function listenUserTickets(user, callback, statusFilter = null) {
  if (!user) {
    callback([]);
    return () => {};
  }
  const uid = user.uid;

  const constraints = [
    where("userId", "==", uid),
    orderBy("updatedAt", "desc"),
    limit(50),
  ];

  if (statusFilter && statusFilter !== "all") {
    constraints.splice(1, 0, where("status", "==", statusFilter));
  }

  const q = query(collection(db, TICKETS), ...constraints);
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => callback([])
  );
}

// ─── Get Ticket ─────────────────────────────────────────────────

/**
 * Get a single ticket by ID (user must own it).
 * @param {string} id
 * @returns {Promise<Object|null>}
 */
export async function getTicket(user, id) {
  if (!user) throw new Error("Authentication required");
  const uid = user.uid;

  const snap = await getDoc(doc(db, TICKETS, id));
  if (!snap.exists()) return null;

  const data = snap.data();
  if (data.userId !== uid) throw new Error("Access denied");

  return { id: snap.id, ...data };
}

// ─── Reply to Ticket ────────────────────────────────────────────

/**
 * Add a user reply to an existing ticket.
 * @param {string} ticketId
 * @param {{ message: string }} payload
 */
export async function replyTicket(user, ticketId, payload) {
  if (!user) throw new Error("Authentication required");
  const uid = user.uid;
  const safeMessage = sanitizeText(payload.message, 2000);
  if (!safeMessage) throw new Error("Message required");

  const ticketRef = doc(db, TICKETS, ticketId);
  const snap = await getDoc(ticketRef);
  if (!snap.exists()) throw new Error("Ticket not found");

  const data = snap.data();
  if (data.userId !== uid) throw new Error("Access denied");

  const replies = data.replies || [];
  replies.push({
    message: safeMessage,
    sentBy: uid,
    sentByEmail: user.email || "",
    isAdmin: false,
    createdAt: new Date().toISOString(),
  });

  await updateDoc(ticketRef, {
    replies,
    lastMessageSnippet: safeMessage.substring(0, 120),
    updatedAt: serverTimestamp(),
  });
}

export default {
  createTicket,
  listUserTickets,
  listenUserTickets,
  getTicket,
  replyTicket,
};



